import type { FocusEvent } from 'react';

import i18n, { TFunction } from 'i18next';
import omitBy from 'lodash-es/omitBy';
import { Path, PathValue, ValidationRule } from 'react-hook-form';
import * as yup from 'yup';

type ErrorMessageOptions = Partial<{
  field: { type?: string; message?: string };
  maxLength: ValidationRule<number>;
  minLength: ValidationRule<number>;
  maxValue: ValidationRule<number>;
  minValue: ValidationRule<number>;
}>;

/**
 * Maps validation rule types to error messages.
 */
export const errorMessage = (options: ErrorMessageOptions) => {
  const { field, maxLength, minLength, maxValue, minValue } = options || {};

  if (!field) {
    return null;
  }

  if (field?.message) {
    return field.message;
  }

  if (field?.type === 'required') {
    return i18n.t('validation.required');
  }

  if (field?.type === 'minLength') {
    return i18n.t('validation.at_least', { 0: minLength?.toString() });
  }

  if (field?.type === 'maxLength') {
    return i18n.t('validation.less_than', { 0: maxLength?.toString() });
  }

  if (field?.type === 'pattern') {
    return i18n.t('validation.pattern');
  }

  if (field?.type === 'maxValue') {
    return i18n.t('validation.max_value', { 0: maxValue?.toString() });
  }

  if (field?.type === 'minValue') {
    return i18n.t('validation.min_value', { 0: minValue?.toString() });
  }

  return null;
};

/**
 * Extracts a specific validation parameter (e.g., "max", "min") from a field within a Yup object schema.
 *
 * This utility inspects the Yup schema description and returns the value of the requested
 * validation parameter for the provided field. It is useful when UI rules (like max length)
 * must be derived directly from an existing Yup schema to avoid duplication.
 *
 * Assumptions:
 * - The `fieldName` exists in the provided `schema` and is a schema that defines tests.
 * - The `paramName` corresponds to a test name that includes a params object containing that key
 *   (e.g., test name "max" with params `{ max: number }`).
 *
 * @template T Schema value type.
 * @param schema A Yup object schema describing the fields and their validations.
 * @param fieldName The name of the field within the schema to inspect.
 * @param paramName The validation parameter name to extract (e.g., "max", "min").
 * @returns The parameter value if found, otherwise `undefined`.
 *
 * @example
 * // Given a schema like:
 * // const schema = yup.object({
 * //   title: yup.string().max(32).required(),
 * // });
 * // getValidationParamFromYupObjectSchema(schema, 'title', 'max') // => 32
 */
export const getValidationParamFromYupObjectSchema = <T>(
  schema: yup.ObjectSchema<T>,
  fieldName: string,
  paramName: string
): number | string | undefined => {
  const fieldsDescription = schema.describe().fields;
  const fieldDescription = fieldsDescription[fieldName] as yup.SchemaDescription;

  const param = fieldDescription.tests.find((x) => x.name === paramName)?.params;

  return param?.[paramName] as number | string | undefined;
};

export const trimOnBlur =
  (field: { onChange: (value: string) => void; onBlur: () => void }) =>
  (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    field.onChange(event.target.value.trim());
    field.onBlur();
  };

/**
 * Removes empty string or undefined values from an object.
 */
export const removeEmptyFields = <T>(data: T): T => {
  return omitBy(data || {}, (v) => v === '' || typeof v === 'undefined') as unknown as T;
};

/**
 * Validates character count limits.
 */
export const charCountValidation = <T>(
  v: PathValue<T, Path<T>>,
  maxCharacters: number,
  t: TFunction,
  shouldValidate: boolean
) => {
  if (!shouldValidate) {
    return true;
  }
  const chars = typeof v === 'string' || Array.isArray(v) ? v?.length || 0 : 0;
  if (chars > maxCharacters) {
    return t('rich_text_editor.errors.char_limit');
  }
  return true;
};
